import React, { useEffect, useState } from 'react';
import { Input, message } from 'antd';
import { ActionButton } from '@doer/frontend-components/buttons';
import { Spinner } from '@doer/frontend-components/spinner';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { CreativityContentData } from '../../../../types/banner';
import { useResponsiveSizes } from '../../../../hooks/use-responsive-sizes';
import { FeedbackRating } from '../../../../../../types/brands/feedback';
import { useSubmitBannerFeedback } from '../../../../hooks/banners/feedback/use-submit-feedback';

type FeedbackType = 'positive' | 'negative' | null;

interface FeedbackCardProps {
  creativity: CreativityContentData;
}

export const FeedbackCard: React.FC<FeedbackCardProps> = ({ creativity }) => {
  const { fontSize, spacing } = useResponsiveSizes();
  const { submitBannerFeedback } = useSubmitBannerFeedback();

  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackType>(null);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    if (!hasInitialized && creativity.feedback?.rating) {
      setSelectedFeedback(creativity.feedback.rating);
      setShowSuccessMessage(true);
      setHasInitialized(true);
    }
    if (!hasInitialized && creativity.feedback?.comment) {
      setComment(creativity.feedback.comment);
    }
  }, [creativity.feedback, hasInitialized]);

  // Inicializar desde feedback existente
  useEffect(() => {
    if (creativity.feedback?.rating) {
      setSelectedFeedback(creativity.feedback.rating);
    }
    if (creativity.feedback?.comment) {
      setComment(creativity.feedback.comment);
    }
  }, [creativity.feedback]);

  const rating: FeedbackRating | null =
    selectedFeedback === 'positive' ? 'positive' : selectedFeedback === 'negative' ? 'negative' : null;

  const handleThumbClick = (type: FeedbackType) => {
    setSelectedFeedback(type === selectedFeedback ? null : type);
    setShowSuccessMessage(false);
  };

  const handleSubmit = async () => {
    if (!rating || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await submitBannerFeedback(creativity.bannerId, rating, comment.trim());
      message.success('¡Gracias por tu feedback!');
      setShowSuccessMessage(true);
    } catch {
      message.error('Error al enviar el feedback. Por favor, intentá nuevamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      style={{
        background: 'rgba(255, 255, 255, 0.7)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        borderRadius: 16,
        padding: spacing.lg,
        border: '1px solid #e2e8f0'
      }}
    >
      {/* Título */}
      <div
        style={{
          fontSize: fontSize.base,
          fontWeight: 500,
          color: '#1e293b',
          marginBottom: spacing.md
        }}
      >
        ¿Qué te pareció este asset?
      </div>

      {/* Botones de feedback */}
      <div style={{ display: 'flex', gap: 12 }}>
        <ActionButton
          variant={selectedFeedback === 'positive' ? 'secondary' : 'outline'}
          onClick={() => handleThumbClick('positive')}
          style={{ flex: 1 }}
        >
          <ThumbsUp size={18} />
          Me gusta
        </ActionButton>

        <ActionButton
          variant={selectedFeedback === 'negative' ? 'secondary' : 'outline'}
          onClick={() => handleThumbClick('negative')}
          style={{ flex: 1 }}
        >
          <ThumbsDown size={18} />
          No me gusta
        </ActionButton>
      </div>

      {/* Textarea y botón de envío */}
      {selectedFeedback && !showSuccessMessage && (
        <div
          style={{
            marginTop: spacing.md,
            display: 'flex',
            flexDirection: 'column',
            gap: spacing.sm
          }}
        >
          <Input.TextArea
            value={comment}
            onChange={e => setComment(e.target.value)}
            placeholder='¿Querés dejar una opinión? (opcional)'
            autoSize={{ minRows: 2, maxRows: 4 }}
            maxLength={150}
            disabled={isSubmitting}
            style={{
              padding: spacing.xs,
              border: '1px solid rgba(102, 126, 234, 0.2)',
              background: 'rgba(255, 255, 255, 0.6)',
              color: '#1e293b',
              borderRadius: 12,
              fontSize: fontSize.base,
              backdropFilter: 'blur(5px)',
              WebkitBackdropFilter: 'blur(5px)',
              resize: 'none'
            }}
          />

          <ActionButton
            variant='primary'
            onClick={handleSubmit}
            disabled={isSubmitting}
            style={{ alignSelf: 'flex-end' }}
          >
            {isSubmitting ? <Spinner size={16} /> : creativity.feedback ? 'Guardar cambios' : 'Enviar feedback'}
          </ActionButton>
        </div>
      )}
    </div>
  );
};
